/**
 * Phase J.3.3 — Modal for creating / editing a billing schedule.
 *
 * Used in two modes:
 *   - create: no `schedule` prop → POSTs to /admin/rentals/{id}/billing-schedule
 *   - edit:   `schedule` prop set → PATCHes /admin/billing-schedules/{id}
 *
 * Renders dynamic config inputs per cadence_kind, and a live "next 6 dates"
 * preview that updates as the form changes (debounced 350ms).
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2, CalendarRange, AlertCircle, Lock } from "lucide-react";
import { toast } from "sonner";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

const CADENCE_KINDS = [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
    { value: "every_n_days", label: "Every N days" },
    { value: "monthly_specific_day", label: "Specific days of month" },
];

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const NET_TERMS = [
    { value: 7, label: "Net 7" },
    { value: 15, label: "Net 15" },
    { value: 30, label: "Net 30" },
    { value: 60, label: "Net 60" },
];

function todayIso() {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${dd}`;
}


function CadenceFields({ cadenceKind, cadenceConfig, setCadenceConfig }) {
    if (cadenceKind === "weekly") {
        return (
            <div className="grid grid-cols-7 gap-1">
                {DOW_LABELS.map((label, i) => (
                    <button
                        key={i}
                        type="button"
                        data-testid={`cadence-weekly-${i}`}
                        onClick={() => setCadenceConfig({ day_of_week: i })}
                        className={
                            "py-2 rounded-md text-xs font-medium ring-1 ring-inset transition-colors " +
                            (cadenceConfig.day_of_week === i
                                ? "bg-blue-900 text-white ring-blue-900"
                                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
                        }
                    >
                        {label}
                    </button>
                ))}
            </div>
        );
    }
    if (cadenceKind === "monthly") {
        return (
            <div className="flex items-center gap-3">
                <Input
                    type="number" min={1} max={31}
                    value={cadenceConfig.day_of_month ?? ""}
                    onChange={(e) => setCadenceConfig({ day_of_month: Number(e.target.value) })}
                    className="w-24"
                    data-testid="cadence-monthly-day"
                />
                <span className="text-xs text-slate-500">Uses last day for shorter months.</span>
            </div>
        );
    }
    if (cadenceKind === "quarterly") {
        return (
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Month in quarter</Label>
                    <Select
                        value={String(cadenceConfig.month_offset ?? 0)}
                        onValueChange={(v) => setCadenceConfig({ ...cadenceConfig, month_offset: Number(v) })}
                    >
                        <SelectTrigger data-testid="cadence-quarterly-offset"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="0">1st month</SelectItem>
                            <SelectItem value="1">2nd month</SelectItem>
                            <SelectItem value="2">3rd month</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs">Day of month</Label>
                    <Input
                        type="number" min={1} max={31}
                        value={cadenceConfig.day_of_month ?? ""}
                        onChange={(e) => setCadenceConfig({ ...cadenceConfig, day_of_month: Number(e.target.value) })}
                        data-testid="cadence-quarterly-day"
                    />
                </div>
            </div>
        );
    }
    if (cadenceKind === "yearly") {
        return (
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Month</Label>
                    <Input
                        type="number" min={1} max={12}
                        value={cadenceConfig.month ?? ""}
                        onChange={(e) => setCadenceConfig({ ...cadenceConfig, month: Number(e.target.value) })}
                        data-testid="cadence-yearly-month"
                    />
                </div>
                <div>
                    <Label className="text-xs">Day of month</Label>
                    <Input
                        type="number" min={1} max={31}
                        value={cadenceConfig.day_of_month ?? ""}
                        onChange={(e) => setCadenceConfig({ ...cadenceConfig, day_of_month: Number(e.target.value) })}
                        data-testid="cadence-yearly-day"
                    />
                </div>
            </div>
        );
    }
    if (cadenceKind === "every_n_days") {
        return (
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs">Every</Label>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number" min={1}
                            value={cadenceConfig.n ?? ""}
                            onChange={(e) => setCadenceConfig({ ...cadenceConfig, n: Number(e.target.value) })}
                            className="w-24"
                            data-testid="cadence-every-n"
                        />
                        <span className="text-xs text-slate-500">days</span>
                    </div>
                </div>
                <div>
                    <Label className="text-xs">Anchor date</Label>
                    <Input
                        type="date"
                        value={cadenceConfig.anchor_date || ""}
                        onChange={(e) => setCadenceConfig({ ...cadenceConfig, anchor_date: e.target.value })}
                        data-testid="cadence-every-anchor"
                    />
                </div>
            </div>
        );
    }
    if (cadenceKind === "monthly_specific_day") {
        const selected = new Set(cadenceConfig.days || []);
        const toggle = (d) => {
            const next = new Set(selected);
            if (next.has(d)) next.delete(d); else next.add(d);
            setCadenceConfig({ days: Array.from(next).sort((a, b) => a - b) });
        };
        return (
            <div className="grid grid-cols-7 gap-1.5">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <button
                        key={d}
                        type="button"
                        data-testid={`cadence-day-${d}`}
                        onClick={() => toggle(d)}
                        className={
                            "py-1.5 rounded text-xs font-medium ring-1 ring-inset transition-colors " +
                            (selected.has(d)
                                ? "bg-blue-900 text-white ring-blue-900"
                                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
                        }
                    >
                        {d}
                    </button>
                ))}
            </div>
        );
    }
    return null;
}


export function BillingScheduleModal({ open, onClose, rentalId, schedule, onSaved }) {
    const isEdit = Boolean(schedule);
    const [cadenceKind, setCadenceKind] = useState(schedule?.cadence_kind || "monthly");
    const [cadenceConfig, setCadenceConfig] = useState(schedule?.cadence_config || { day_of_month: 1 });
    const [amountDollars, setAmountDollars] = useState(
        schedule ? (schedule.amount_cents / 100).toFixed(2) : "",
    );
    const [description, setDescription] = useState(schedule?.description || "");
    const [paymentTerms, setPaymentTerms] = useState(schedule?.payment_terms_days || 30);
    const [startsAt, setStartsAt] = useState(schedule?.starts_at || todayIso());
    const [endDate, setEndDate] = useState(schedule?.end_date || "");
    const [autoCharge, setAutoCharge] = useState(Boolean(schedule?.auto_charge));
    const [notes, setNotes] = useState(schedule?.notes || "");
    const [prorateFirst, setProrateFirst] = useState(Boolean(schedule?.prorate_first_period));
    // Phase B1 — once the first prorated invoice has fired the toggle freezes.
    const prorateFrozen = Boolean(schedule?.first_prorated_invoice_id);
    const [preview, setPreview] = useState([]);
    const [previewError, setPreviewError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Phase J.3.4 — saved payment methods for the rental's customer org.
    const [customerOrgId, setCustomerOrgId] = useState(null);
    const [savedMethods, setSavedMethods] = useState([]);
    const [defaultPmId, setDefaultPmId] = useState(null);
    const [methodsLoading, setMethodsLoading] = useState(true);

    // Resolve the rental's customer org id then fetch its saved methods.
    useEffect(() => {
        let cancelled = false;
        async function loadMethods() {
            setMethodsLoading(true);
            try {
                let orgId = null;
                if (rentalId) {
                    const { data: r } = await api.get(`/admin/rentals/${rentalId}`);
                    orgId = r?.customer_id || r?.customer_org_id || null;
                }
                if (!orgId) {
                    setSavedMethods([]); setDefaultPmId(null); return;
                }
                if (cancelled) return;
                setCustomerOrgId(orgId);
                const { data: pms } = await api.get(
                    `/admin/customers/${orgId}/payment-methods`
                );
                if (cancelled) return;
                setSavedMethods(pms?.items || []);
                setDefaultPmId(pms?.default_payment_method_id || null);
            } catch {
                if (!cancelled) {
                    setSavedMethods([]); setDefaultPmId(null);
                }
            } finally {
                if (!cancelled) setMethodsLoading(false);
            }
        }
        if (open) loadMethods();
        return () => { cancelled = true; };
    }, [open, rentalId]);

    const defaultMethod = useMemo(
        () => savedMethods.find((m) => m.id === defaultPmId) || null,
        [savedMethods, defaultPmId],
    );
    const hasMethod = savedMethods.length > 0;

    const onCadenceKindChange = (val) => {
        setCadenceKind(val);
        // Reset config to a sensible default for the new kind.
        if (val === "weekly") setCadenceConfig({ day_of_week: 0 });
        else if (val === "monthly") setCadenceConfig({ day_of_month: 1 });
        else if (val === "quarterly") setCadenceConfig({ month_offset: 0, day_of_month: 1 });
        else if (val === "yearly") setCadenceConfig({ month: 1, day_of_month: 1 });
        else if (val === "every_n_days") setCadenceConfig({ n: 14, anchor_date: startsAt });
        else if (val === "monthly_specific_day") setCadenceConfig({ days: [1, 15] });
    };

    // Debounced preview refresh.
    useEffect(() => {
        let cancelled = false;
        setPreviewError(null);
        if (!cadenceKind || !startsAt) return;
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post(
                    "/admin/billing-schedules/preview-next-n",
                    {
                        cadence_kind: cadenceKind,
                        cadence_config: cadenceConfig,
                        starts_at: startsAt,
                        n: 6,
                    },
                );
                if (!cancelled) setPreview(data.dates || []);
            } catch (e) {
                if (!cancelled) {
                    setPreview([]);
                    setPreviewError(formatApiError(e));
                }
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
    }, [cadenceKind, cadenceConfig, startsAt]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const amount_cents = Math.round(Number(amountDollars) * 100);
            const payload = {
                cadence_kind: cadenceKind,
                cadence_config: cadenceConfig,
                amount_cents,
                description,
                payment_terms_days: Number(paymentTerms),
                starts_at: startsAt,
                end_date: endDate || null,
                auto_charge: autoCharge,
                payment_method_id: autoCharge ? (defaultPmId || null) : null,
                notes: notes || null,
                prorate_first_period: prorateFirst,
            };
            if (isEdit) {
                const { data } = await api.patch(`/admin/billing-schedules/${schedule.id}`, payload);
                onSaved && onSaved(data);
                toast.success("Recurring billing updated");
            } else {
                const { data } = await api.post(
                    `/admin/rentals/${rentalId}/billing-schedule`,
                    payload,
                );
                onSaved && onSaved(data);
                toast.success("Recurring billing set up");
            }
            onClose && onClose();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }, [amountDollars, cadenceKind, cadenceConfig, description, paymentTerms, startsAt, endDate, autoCharge, notes, prorateFirst, isEdit, schedule, rentalId, onSaved, onClose]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="billing-schedule-modal"
            >
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit recurring billing" : "Set up recurring billing"}</DialogTitle>
                    <DialogDescription>
                        Define how invoices auto-issue for this rental. You can pause, resume, or end at any time.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                    <section className="space-y-2">
                        <Label htmlFor="cadence-kind">Cadence</Label>
                        <Select value={cadenceKind} onValueChange={onCadenceKindChange}>
                            <SelectTrigger data-testid="cadence-kind-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CADENCE_KINDS.map((c) => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="mt-2">
                            <CadenceFields
                                cadenceKind={cadenceKind}
                                cadenceConfig={cadenceConfig}
                                setCadenceConfig={setCadenceConfig}
                            />
                        </div>
                    </section>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="amount">Amount per cycle (USD)</Label>
                            <Input
                                id="amount"
                                type="number" min={0} step={0.01}
                                value={amountDollars}
                                onChange={(e) => setAmountDollars(e.target.value)}
                                placeholder="500.00"
                                data-testid="schedule-amount"
                            />
                        </div>
                        <div>
                            <Label htmlFor="terms">Payment terms</Label>
                            <Select value={String(paymentTerms)} onValueChange={(v) => setPaymentTerms(Number(v))}>
                                <SelectTrigger data-testid="schedule-terms"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {NET_TERMS.map((t) => (
                                        <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="desc">Line-item description</Label>
                        <Textarea
                            id="desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g. Monthly rent for Reefer #22"
                            rows={2}
                            data-testid="schedule-description"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="starts">Start date</Label>
                            <Input
                                id="starts" type="date"
                                value={startsAt}
                                onChange={(e) => setStartsAt(e.target.value)}
                                data-testid="schedule-starts"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ends">End date (optional)</Label>
                            <Input
                                id="ends" type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                placeholder="Never"
                                data-testid="schedule-end"
                            />
                        </div>
                    </div>

                    <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 px-3 py-3 flex items-start gap-3">
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-900">Auto-charge</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Charge the customer's saved Stripe ACH account automatically once an invoice issues.
                            </p>
                            {!methodsLoading && hasMethod && defaultMethod && (
                                <p
                                    data-testid="schedule-auto-charge-method-hint"
                                    className="text-xs text-slate-700 mt-2 inline-flex items-center gap-1.5"
                                >
                                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-900 ring-1 ring-inset ring-emerald-200">
                                        {defaultMethod.bank_name || "Bank"} ••••{defaultMethod.last4}
                                    </span>
                                    <span className="text-slate-500">will be debited.</span>
                                </p>
                            )}
                            {!methodsLoading && !hasMethod && autoCharge && (
                                <p
                                    data-testid="schedule-auto-charge-no-method-warning"
                                    className="text-xs text-amber-800 mt-2 rounded-md bg-amber-50 ring-1 ring-amber-200 px-2 py-1.5"
                                >
                                    <AlertCircle size={11} className="inline mr-1 -mt-0.5" />
                                    This customer has no saved bank account. The schedule
                                    will save with auto-charge ON but charges will be skipped
                                    until they add a bank account.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={autoCharge}
                                onCheckedChange={setAutoCharge}
                                data-testid="schedule-auto-charge"
                            />
                        </div>
                    </div>

                    <div className="rounded-md bg-slate-50 ring-1 ring-slate-200 px-3 py-3 flex items-start gap-3">
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-900">Prorate first invoice</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                If the rental started mid-period, charge a prorated amount for the partial first month, then resume full monthly billing on the day of the month.
                            </p>
                            {prorateFrozen && (
                                <p
                                    data-testid="schedule-prorate-frozen"
                                    className="text-xs text-slate-600 mt-2 rounded-md bg-slate-100 ring-1 ring-slate-200 px-2 py-1.5"
                                >
                                    <Lock size={10} className="inline mr-1 -mt-0.5" />
                                    The first prorated invoice has already been issued. This setting is locked.
                                </p>
                            )}
                            {prorateFirst && !prorateFrozen && (() => {
                                const cents = Math.round(Number(amountDollars || 0) * 100);
                                let firstFire = null;
                                if (preview && preview.length > 0) firstFire = preview[0];
                                let days = 0;
                                if (startsAt && firstFire) {
                                    const start = new Date(startsAt + "T00:00:00");
                                    const fire = new Date(firstFire + "T00:00:00");
                                    days = Math.floor((fire - start) / 86_400_000);
                                }
                                if (days <= 0 || cents <= 0) {
                                    return (
                                        <p
                                            data-testid="schedule-prorate-preview"
                                            className="text-xs text-blue-900 mt-2 rounded-md bg-blue-50 ring-1 ring-blue-200 px-2 py-1.5"
                                        >
                                            $0.00 — full month due (start day aligns with cadence; no proration needed).
                                        </p>
                                    );
                                }
                                const proratedCents = Math.round(cents * days / 30);
                                const amount = (proratedCents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return (
                                    <p
                                        data-testid="schedule-prorate-preview"
                                        className="text-xs text-blue-900 mt-2 rounded-md bg-blue-50 ring-1 ring-blue-200 px-2 py-1.5"
                                    >
                                        First invoice ≈ <span className="font-semibold">${amount}</span> ({days} day{days === 1 ? "" : "s"} × ${(cents / 100 / 30).toFixed(2)}/day). Subsequent invoices: ${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })} on day {cadenceConfig.day_of_month ?? "—"}.
                                    </p>
                                );
                            })()}
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={prorateFirst}
                                onCheckedChange={setProrateFirst}
                                disabled={prorateFrozen}
                                data-testid="schedule-prorate-first"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="notes">Admin notes (private)</Label>
                        <Textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            data-testid="schedule-notes"
                        />
                    </div>

                    <section
                        data-testid="schedule-preview"
                        className="rounded-md bg-blue-50/60 ring-1 ring-blue-200 px-3 py-3"
                    >
                        <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                            <CalendarRange size={14} aria-hidden="true" />
                            Next 6 invoices would auto-issue on:
                        </div>
                        {previewError ? (
                            <p className="text-xs text-rose-700 mt-2">{previewError}</p>
                        ) : preview.length === 0 ? (
                            <p className="text-xs text-slate-500 mt-2">Computing…</p>
                        ) : (
                            <ol className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm text-slate-800">
                                {preview.map((d, i) => (
                                    <li
                                        key={d + i}
                                        data-testid={`schedule-preview-${i}`}
                                        className="font-mono tracking-tight"
                                    >
                                        <span className="text-slate-400 mr-2">{String(i + 1).padStart(2, "0")}.</span>
                                        {d}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </section>

                    {error && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-start gap-2">
                            <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="schedule-cancel">Cancel</Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || !cadenceKind || !amountDollars || !description || !startsAt}
                        data-testid="schedule-save"
                        className="gap-2"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
                        {isEdit ? "Save changes" : "Set up recurring billing"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BillingScheduleModal;
